/**
 * Studio AI gallery thumbnail cache.
 *
 * Mirrors the slide-sense pattern (see use-thumbnail-prefetch.ts on that
 * feature). The gallery used to render <img src="/api/studio-ai/images/<id>">
 * for every recent creation; img tags can't carry the Authorization
 * header so the only auth they had was the EasyAuth session cookie. In
 * local dev there's no equivalent cookie and every thumbnail 401'd; in
 * prod the cookie sometimes doesn't reach (reload edge cases, third-party
 * context). Result: most thumbnails blank, some occasionally working.
 *
 * Fix: pull image bytes via the bearer-authenticated XHR batch endpoint,
 * decode each to a Blob, URL.createObjectURL → store in this Zustand
 * cache. The hook then hands the resulting blob: URL to the <img> tag,
 * which renders without any auth headers. Same code path everywhere.
 *
 * Object URLs are revoked when overwritten or explicitly removed so long
 * Studio AI sessions do not retain every full-resolution image blob forever.
 */

import { useEffect } from "react"

import { create } from "zustand"

import { fetchImagesBatch } from "@/api/studio-ai-api"

const BATCH_SIZE = 50
const MAX_CONCURRENT_BATCHES = 2
// How long a transient (network / 5xx) failure is considered "do not retry yet".
// After this window elapses the id becomes eligible for a retry on the next
// prefetch trigger. Keep this short — the cost of a retry is one batched
// request, and the cost of NOT retrying is a permanently broken gallery card.
const TRANSIENT_RETRY_COOLDOWN_MS = 10_000
// Cap how many times we'll retry a transient failure before promoting it to
// permanent. Without this, a backend that is genuinely down would keep getting
// hammered every time the user opened the gallery.
const MAX_TRANSIENT_RETRIES = 3

function classifyError(error: unknown): { kind: "permanent" | "transient"; status?: number } {
  if (!(error instanceof Error)) return { kind: "transient" }
  const match = error.message.match(/status(?:\s+code)?[:\s]+(\d{3})/i)
  if (!match) {
    // No HTTP status parsed — almost certainly a network failure (fetch threw).
    return { kind: "transient" }
  }
  const status = Number(match[1])
  // 4xx → the server has a definitive answer (auth, not found, bad request).
  // Re-firing will not change the outcome. 5xx and 408/429 are transient.
  if (status === 408 || status === 429 || status >= 500) {
    return { kind: "transient", status }
  }
  return { kind: "permanent", status }
}

function revokeObjectUrl(url: string | undefined): void {
  if (!url?.startsWith("blob:")) return
  if (typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(url)
  }
}

interface TransientFailure {
  cooldownUntil: number
  attempts: number
}

interface ImageThumbnailCacheState {
  urls: Map<string, string>
  inFlight: Set<string>
  /** Ids the backend reported as definitively unavailable (4xx). Never retried. */
  failed: Set<string>
  /** Ids that failed transiently (network/5xx). Retry-eligible after cooldown. */
  transient: Map<string, TransientFailure>
  setUrl: (jobId: string, url: string) => void
  setUrls: (entries: Array<[string, string]>) => void
  removeUrl: (jobId: string) => void
  markInFlight: (jobIds: string[]) => void
  clearInFlight: (jobIds: string[]) => void
  markFailed: (jobIds: string[]) => void
  markTransient: (jobIds: string[]) => void
  has: (jobId: string) => boolean
  get: (jobId: string) => string | undefined
  isPending: (jobId: string) => boolean
  hasFailed: (jobId: string) => boolean
  /** True if id is currently in transient-failure cooldown (i.e. not retry-eligible). */
  isInCooldown: (jobId: string) => boolean
  cooldownRemainingMs: (jobId: string) => number
}

export const useImageThumbnailCache = create<ImageThumbnailCacheState>((set, get) => ({
  urls: new Map(),
  inFlight: new Set(),
  failed: new Set(),
  transient: new Map(),
  setUrl: (jobId, url) =>
    set((state) => {
      const next = new Map(state.urls)
      const previousUrl = next.get(jobId)
      if (previousUrl !== url) {
        revokeObjectUrl(previousUrl)
      }
      next.set(jobId, url)
      return { urls: next }
    }),
  setUrls: (entries) =>
    set((state) => {
      if (entries.length === 0) return state
      const next = new Map(state.urls)
      // Writing a URL clears any stale failure flag for that id — otherwise a
      // transient earlier failure followed by a success would still report
      // hasFailed=true and consumers would hide the image.
      const nextFailed = new Set(state.failed)
      const nextTransient = new Map(state.transient)
      for (const [id, url] of entries) {
        const previousUrl = next.get(id)
        if (previousUrl !== url) {
          revokeObjectUrl(previousUrl)
        }
        next.set(id, url)
        nextFailed.delete(id)
        nextTransient.delete(id)
      }
      return { urls: next, failed: nextFailed, transient: nextTransient }
    }),
  removeUrl: (jobId) =>
    set((state) => {
      const previousUrl = state.urls.get(jobId)
      revokeObjectUrl(previousUrl)
      const nextUrls = new Map(state.urls)
      nextUrls.delete(jobId)
      const nextInFlight = new Set(state.inFlight)
      nextInFlight.delete(jobId)
      const nextFailed = new Set(state.failed)
      nextFailed.delete(jobId)
      const nextTransient = new Map(state.transient)
      nextTransient.delete(jobId)
      return {
        urls: nextUrls,
        inFlight: nextInFlight,
        failed: nextFailed,
        transient: nextTransient,
      }
    }),
  markInFlight: (jobIds) =>
    set((state) => {
      if (jobIds.length === 0) return state
      const next = new Set(state.inFlight)
      for (const id of jobIds) next.add(id)
      return { inFlight: next }
    }),
  clearInFlight: (jobIds) =>
    set((state) => {
      if (jobIds.length === 0) return state
      const next = new Set(state.inFlight)
      for (const id of jobIds) next.delete(id)
      return { inFlight: next }
    }),
  markFailed: (jobIds) =>
    set((state) => {
      if (jobIds.length === 0) return state
      const next = new Set(state.failed)
      const nextTransient = new Map(state.transient)
      for (const id of jobIds) {
        next.add(id)
        // Permanent failure supersedes any transient state.
        nextTransient.delete(id)
      }
      return { failed: next, transient: nextTransient }
    }),
  markTransient: (jobIds) =>
    set((state) => {
      if (jobIds.length === 0) return state
      const next = new Map(state.transient)
      const promoteToPermanent: string[] = []
      const now = Date.now()
      for (const id of jobIds) {
        const prior = next.get(id)
        const attempts = (prior?.attempts ?? 0) + 1
        if (attempts > MAX_TRANSIENT_RETRIES) {
          promoteToPermanent.push(id)
          next.delete(id)
        } else {
          next.set(id, { cooldownUntil: now + TRANSIENT_RETRY_COOLDOWN_MS, attempts })
        }
      }
      if (promoteToPermanent.length === 0) {
        return { transient: next }
      }
      const nextFailed = new Set(state.failed)
      for (const id of promoteToPermanent) nextFailed.add(id)
      return { transient: next, failed: nextFailed }
    }),
  has: (jobId) => get().urls.has(jobId),
  get: (jobId) => get().urls.get(jobId),
  isPending: (jobId) => get().inFlight.has(jobId),
  hasFailed: (jobId) => get().failed.has(jobId),
  isInCooldown: (jobId) => {
    const entry = get().transient.get(jobId)
    if (!entry) return false
    if (entry.cooldownUntil <= Date.now()) return false
    return true
  },
  cooldownRemainingMs: (jobId) => {
    const entry = get().transient.get(jobId)
    if (!entry) return 0
    return Math.max(0, entry.cooldownUntil - Date.now())
  },
}))

async function base64ToBlob(base64: string, contentType: string): Promise<Blob> {
  const response = await fetch(`data:${contentType};base64,${base64}`)
  return response.blob()
}

export async function prefetchImageThumbnails(jobIds: string[]): Promise<void> {
  if (jobIds.length === 0) return
  const cache = useImageThumbnailCache.getState()
  const missing = jobIds.filter((id) => !cache.has(id) && !cache.isPending(id))
  if (missing.length === 0) return

  cache.markInFlight(missing)

  const chunks: string[][] = []
  for (let i = 0; i < missing.length; i += BATCH_SIZE) {
    chunks.push(missing.slice(i, i + BATCH_SIZE))
  }

  for (let i = 0; i < chunks.length; i += MAX_CONCURRENT_BATCHES) {
    const batch = chunks.slice(i, i + MAX_CONCURRENT_BATCHES)
    await Promise.all(
      batch.map(async (chunk) => {
        try {
          const response = await fetchImagesBatch(chunk)
          const entries: Array<[string, string]> = []
          for (const img of response.images) {
            const blob = await base64ToBlob(img.data_b64, img.content_type)
            entries.push([img.job_id, URL.createObjectURL(blob)])
          }
          useImageThumbnailCache.getState().setUrls(entries)
          if (response.missing.length > 0) {
            useImageThumbnailCache.getState().markFailed(response.missing)
          }
        } catch (error) {
          // Distinguish permanent (4xx — server has a definitive answer) from
          // transient (network/5xx/429) failures. Marking the entire chunk
          // permanently failed on a single network blip used to brick 50
          // gallery cards until page reload.
          const classified = classifyError(error)
          if (classified.kind === "permanent") {
            // eslint-disable-next-line no-console
            console.warn(
              `[image-thumbnail-prefetch] permanent failure (status ${classified.status}) for ${chunk.length} ids`,
              error
            )
            useImageThumbnailCache.getState().markFailed(chunk)
          } else {
            // eslint-disable-next-line no-console
            console.warn(
              `[image-thumbnail-prefetch] transient failure${
                classified.status ? ` (status ${classified.status})` : ""
              } for ${chunk.length} ids — will retry after cooldown`,
              error
            )
            useImageThumbnailCache.getState().markTransient(chunk)
          }
        } finally {
          useImageThumbnailCache.getState().clearInFlight(chunk)
        }
      })
    )
  }
}

/**
 * Run once per gallery list update. Skips ids already in cache, so
 * re-renders and pagination only fetch what's new.
 */
export function useImageThumbnailPrefetch(jobIds: ReadonlyArray<string> | undefined): void {
  // We previously cached "seen" ids in a ref to avoid re-walking the list on
  // every render. That ref was a permanent decision — once seen, an id was
  // never reconsidered. With the new transient-failure cooldown we need ids
  // to become eligible for retry once their cooldown expires, so we no
  // longer short-circuit on the ref. The cache itself (`has` / `hasFailed`
  // / `isInCooldown` / `isPending`) is the source of truth.
  useEffect(() => {
    if (!jobIds || jobIds.length === 0) return
    const cache = useImageThumbnailCache.getState()
    const fresh: string[] = []
    for (const id of jobIds) {
      if (!id) continue
      if (cache.has(id)) continue
      if (cache.hasFailed(id)) continue
      if (cache.isPending(id)) continue
      if (cache.isInCooldown(id)) continue
      fresh.push(id)
    }
    if (fresh.length > 0) {
      void prefetchImageThumbnails(fresh)
    }
  }, [jobIds])
}
